import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams, useLocation } from 'react-router-dom'
import { useFeatureFlags } from 'components/LaunchDarkly/LaunchDarklyProvider'
import { isNil } from 'lodash'
import { useIdentity } from 'pages/Identity/Login'
import { Grid, Typography, FormControlLabel, Switch, Select, MenuItem, FormControl, InputLabel, Box, Alert, Snackbar, Menu, MenuItem as MenuItemComponent, useTheme, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import UploadIcon from '@mui/icons-material/Upload'
import { List } from 'components/Listing/List'
import Loader from 'components/Loader'
import { fetchCandidateJobs, fetchHospitalJobs, fetchPublicJobs } from 'src/redux/jobs'
import { fetchJobFilterValues } from 'src/redux/filters'
import JobContent from './JobContent'
import { useJobs } from './useJobs'
import JobDrawer from 'components/Drawers/JobDrawer/JobDrawer'
import SmartUploadDrawer from 'components/Drawers/SmartUploadDrawer/SmartUploadDrawer'
import { Filters } from '../../components/Filters'
import { FilterType } from '../../components/Filters/filterDefinitions'
import { candidateTypeDefinitions } from 'components/Filters/candidateTypeDefinitions'
import { filterDefinitions } from 'components/Filters/filterDefinitions'
import { fetchUpdateCandidateJob } from 'src/redux/candidateJob'
import { applyFilters } from 'components/Filters/helpers'
import { get as getConfig } from 'config'

// AI Components
import AIJobInterface from './AI/AIJobInterface'
import { updateAiJobFavorite, setAIMode } from '../../redux/aiJobSearch'

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    color: theme.palette.grey[400],
    '&.Mui-checked': {
      color: theme.palette.error.main,
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.error.light,
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.grey[300],
  },
}))


const Jobs = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const signedIn = useSelector(state => state.identity.signedIn)

  // Config flags for AI features
  const { candidateAiSearch } = useFeatureFlags()
  const allowAIJobSearch = candidateAiSearch ?? getConfig('allowAIJobSearch', false)
  const candidate = useSelector(state => state.candidate)
  const {
    candidateType,
    specialties = [],
    loading: candidateLoading,
  } = candidate
  const { loading: employerUserLoading } = useSelector(state => state.employerUser)
  const loading = candidateLoading || employerUserLoading
  const { employerId, employerUserId, candidateId, userId, isEmployerAdmin } = useIdentity()
  const [activeItem, setActiveItem] = useState()
  const [page, setPage] = useState(1)
  const pageSize = 50
  const { jobsList = [], isLoadingJobs } = useSelector(state => state.jobs)
  const { isLoadingFilters } = useSelector(state => state.filters).loading
  const filterValues = useSelector(state => state.filters.jobFilterValues) || {}
  const totalJobs = useSelector(state => state.jobs.totalJobs)
  const totalPages = Math.ceil(totalJobs / pageSize)
  const dataCall = employerUserId ? fetchHospitalJobs : signedIn ? fetchCandidateJobs : fetchPublicJobs
  const [searchParams, setSearchParams] = useSearchParams()
  const candidateTypeId = searchParams.get('candidateTypeId')
  const [displayedData, setDisplayedData] = useState(jobsList)
  const [isApplyingFilters, setIsApplyingFilters] = useState(false)

  // Added state for the "Favorites Only" filter
  const [favoritesOnly, setFavoritesOnly] = useState(false)

  const [archivedFilter, setArchivedFilter] = useState('all')

  // AI-related state from Redux
  const isAIMode = useSelector(state => state.aiJobSearch.isAIMode)

  // Smart Job Upload notification state
  const [showUploadSuccess, setShowUploadSuccess] = useState(false)
  const [uploadedJobsCount, setUploadedJobsCount] = useState(0)

  // Upload menu state
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null)

  // Job drawer state (for manual upload)
  const { drawerJobOpen, setDrawerJobOpen } = useJobs()

  // Smart upload drawer state
  const [smartUploadDrawerOpen, setSmartUploadDrawerOpen] = useState(false)

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('jobsLeftPanelWidth')
    // Default 30% of typical viewport width (assuming ~1400px width = ~420px)
    return saved ? parseInt(saved) : 420
  })
  const [isResizing, setIsResizing] = useState(false)

  const startResize = (e) => {
    if (mobile) return
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const container = document.getElementById('jobs-container')
        if (!container) return

        const containerRect = container.getBoundingClientRect()
        const newWidth = e.clientX - containerRect.left

        if (newWidth >= 250 && newWidth <= 1000) {
          setLeftPanelWidth(newWidth)
        }
      })
    }

    const stopResize = () => {
      setIsResizing(false)
      localStorage.setItem('jobsLeftPanelWidth', leftPanelWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isResizing, leftPanelWidth, mobile])

  const handleListItemClick = currId => {
    const activeJob = !isNil(currId)
      ? displayedData.find(j => `${j.id}` === `${currId}`)
      : displayedData.length > 0
        ? displayedData[0]
        : null
    setActiveItem(activeJob)
  }

  useEffect(() => {
    if (!signedIn && searchParams.size === 0) {
      dispatch(dataCall({ page, pageSize }))
    }
  }, [signedIn, dispatch, dataCall, page, pageSize])

  useEffect(() => {
    if (!mobile && jobsList.length > 0) {
      setActiveItem(jobsList[0])
    } else {
      setActiveItem(null)
    }
  }, [jobsList, mobile])

  useEffect(() => {
    if (location.state?.newlyUploaded) {
      setUploadedJobsCount(location.state.newlyUploaded)
      setShowUploadSuccess(true)
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  useEffect(() => {
    if (candidateType && candidateType.id && searchParams.size === 0) {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('candidateTypeId', candidateType.id)
      const candidateTypeDefinition = candidateTypeDefinitions.find(
        def => def.id === candidateType.id,
      )
      const specialtyFilterDefinition = filterDefinitions.find(
        filter => filter.name === 'specialty',
      )
      if (candidateTypeDefinition?.useCandidateSpecialty === true) {
        if (specialties.length > 0) {
          newParams.set(specialtyFilterDefinition.queryStringKey, specialties[0].id)
        } else {
          newParams.delete(specialtyFilterDefinition.queryStringKey)
        }
      } else {
        newParams.delete(specialtyFilterDefinition.queryStringKey)
      }
      setSearchParams(newParams)
    }
  }, [candidateType, specialties])

  useEffect(() => {
    if (candidateTypeId) {
      const specialtyIdsParam = searchParams.get('specialtyIds')
      dispatch(
        fetchJobFilterValues({
          employerId: employerId,
          candidateTypeId: searchParams.get('candidateTypeId'),
          specialtyIds: specialtyIdsParam ? specialtyIdsParam.split('|') : [],
        }),
      )
    }
    else {
      dispatch(
        fetchJobFilterValues({
          employerId: employerId,
        }),
      )
    }
  }, [candidateTypeId, employerId])

  // Update displayedData when jobsList changes
  useEffect(() => {
    setDisplayedData(jobsList)
  }, [jobsList])

  // Handle URL parameter changes, pagination, and toggle filters
  useEffect(() => {
    if ((searchParams.size > 0 || employerUserId) && !isAIMode) {
      const effectiveParams = new URLSearchParams(searchParams)
      if (favoritesOnly) effectiveParams.set('favorite', 'true')
      if (archivedFilter !== 'all') effectiveParams.set('archived', archivedFilter)

      setIsApplyingFilters(true)
      applyFilters({
        candidateId,
        employerUserId,
        searchParams: effectiveParams,
        dispatch,
        getData: dataCall,
        page,
        pageSize,
        employerId,
        isEmployerAdmin,
        userId,
      })
      const timer = setTimeout(() => {
        setIsApplyingFilters(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [searchParams, isAIMode, page, favoritesOnly, archivedFilter])

  const toggleFavoritesFilter = () => {
    setFavoritesOnly(!favoritesOnly)
  }

  const handleArchivedFilterChange = (event) => {
    setArchivedFilter(event.target.value)
  }

  const toggleAIMode = () => {
    dispatch(setAIMode(!isAIMode))
  }

  // Upload functionality handlers
  const handleUploadMenuClick = (event) => {
    setUploadMenuAnchor(event.currentTarget)
  }

  const handleUploadMenuClose = () => {
    setUploadMenuAnchor(null)
  }

  const handleSmartUpload = () => {
    handleUploadMenuClose()
    setSmartUploadDrawerOpen(true)
  }

  const handleManualUpload = () => {
    handleUploadMenuClose()
    setDrawerJobOpen(true)
  }

  const showAIToggle = signedIn && !employerUserId && allowAIJobSearch

  const leftPanelHeader = 'Results: ' + `${totalJobs ? totalJobs.toLocaleString() : 0}` + ' Jobs'

  const applyJobFilters = ({ searchParams }) => {
    if (!isAIMode) {
      const effectiveParams = new URLSearchParams(searchParams)
      if (favoritesOnly) effectiveParams.set('favorite', 'true')
      if (archivedFilter !== 'all') effectiveParams.set('archived', archivedFilter)

      applyFilters({
        candidateId,
        employerUserId,
        searchParams: effectiveParams,
        dispatch,
        getData: dataCall,
        page,
        pageSize,
        employerId,
        isEmployerAdmin,
        userId,
      })
    }
  }

  const onFavoriteClick = ({ favorite, jobId }) => {
    if (isAIMode) {
      // For AI mode, update AI job data in Redux
      dispatch(updateAiJobFavorite({ jobId, favorite }))
    } else {
      // For regular mode, update displayedData
      const updatedData = displayedData.map(item =>
        item.id === jobId
          ? {
              ...item,
              candidateJob: {
                ...item.candidateJob,
                favorite,
              },
            }
          : item,
      )

      setDisplayedData(updatedData)

      if (activeItem && activeItem.id === jobId) {
        const updatedActiveItem = {
          ...activeItem,
          candidateJob: {
            ...activeItem.candidateJob,
            favorite,
          },
        }
        setActiveItem(updatedActiveItem)
      }
    }

    dispatch(fetchUpdateCandidateJob({ candidateId, jobId, favorite }))
  }

  return (
    <>
      <Grid
        container
        justifyContent='center'
        className="ai-smart-search-container"
        sx={{
          marginRight: 0,
          transition: 'margin-right 0.3s ease',
        }}
      >
        <Grid item xs={12}>
          {!isAIMode && (
            <Filters
              filterType={FilterType.JOB}
              filterValues={filterValues}
              listType='job'
              loading={isLoadingFilters}
              applyFilters={applyJobFilters}
            />
          )}
        </Grid>

        {/* Full-width search mode tabs */}
        {showAIToggle && (
          <Grid item xs={12} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                borderBottom: `2px solid ${theme.palette.divider}`,
                backgroundColor: 'white',
              }}
            >
              <Box
                onClick={() => isAIMode && toggleAIMode()}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  padding: '14px 24px',
                  cursor: 'pointer',
                  borderBottom: !isAIMode ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                  backgroundColor: !isAIMode ? `${theme.palette.primary.main}08` : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: !isAIMode ? `${theme.palette.primary.main}08` : theme.palette.grey[100],
                    borderBottom: !isAIMode ? `3px solid ${theme.palette.primary.main}` : `3px solid ${theme.palette.grey[400]}`,
                  },
                }}
              >
                <Typography sx={{
                  fontSize: '0.95rem',
                  fontWeight: !isAIMode ? 700 : 500,
                  color: !isAIMode ? theme.palette.primary.main : theme.palette.text.secondary,
                }}>
                  Traditional Search
                </Typography>
              </Box>
              <Box
                onClick={() => !isAIMode && toggleAIMode()}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  padding: '14px 24px',
                  cursor: 'pointer',
                  borderBottom: isAIMode ? `3px solid ${theme.palette.ai.main}` : '3px solid transparent',
                  backgroundColor: isAIMode ? `${theme.palette.ai.main}0A` : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isAIMode ? `${theme.palette.ai.main}0A` : `${theme.palette.ai.main}12`,
                    borderBottom: isAIMode ? `3px solid ${theme.palette.ai.main}` : `3px solid ${theme.palette.ai.main}80`,
                  },
                }}
              >
                {!isAIMode && (
                  <Typography sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'white',
                    backgroundColor: theme.palette.ai.main,
                    borderRadius: '4px',
                    padding: '1px 6px',
                  }}>
                    NEW
                  </Typography>
                )}
                <AutoAwesomeIcon sx={{
                  fontSize: '1.1rem',
                  color: isAIMode ? theme.palette.ai.main : theme.palette.text.secondary,
                }} />
                <Typography sx={{
                  fontSize: '0.95rem',
                  fontWeight: isAIMode ? 700 : 500,
                  color: isAIMode ? theme.palette.ai.main : theme.palette.text.secondary,
                }}>
                  {isAIMode ? 'AI Search' : 'Try AI Search'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}

        {(isLoadingJobs || loading || isApplyingFilters) && !isAIMode ? (
          <Loader />
        ) : isAIMode ? (
          <AIJobInterface />
        ) : (
          <Box id="jobs-container" sx={{
            display: 'flex',
            width: '100%',
            maxWidth: '100vw',
            overflow: 'hidden',
            px: { xs: 0, md: 4 },
          }}>
            <Grid
              container
              item
              xs={12}
              direction='column'
              alignContent='flex-start'
              sx={{
                height: 'calc(100vh - 64px)',
                minHeight: 'calc(100vh - 64px)',
                flex: { xs: '1', md: `0 0 ${leftPanelWidth}px` },
                width: { xs: '100%', md: 'auto' },
                overflow: 'hidden',
                px: { xs: 0 },
                '& .MuiBox-root': {
                  px: { xs: 0, md: 3 }
                }
              }}
            >
              {/* Consistent Header Structure */}
              <Grid
                container
                item
                alignItems='center'
                justifyContent='space-between'
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                  px: { xs: 2 },
                  py: 2, // Consistent padding
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)', // Visual separator under Results section
                  backgroundColor: 'white', // Consistent background
                  '& .MuiBox-root': {
                    px: { xs: 0, md: 3 }
                  }
                }}
              >
                <Grid container item xs={12} alignItems="center" justifyContent="space-between">
                  <Grid item xs="auto">
                    <Typography variant='h6' sx={{
                      fontWeight: 'bold',
                    }}>
                      {leftPanelHeader}
                    </Typography>
                  </Grid>
                  <Grid container item xs="auto" spacing={2} justifyContent="flex-end" alignItems="center">
                    {signedIn && employerUserId && (
                      <Grid item>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                          <Select
                            value={archivedFilter}
                            onChange={handleArchivedFilterChange}
                            label="Status"
                            sx={{
                              fontSize: '0.875rem',
                              height: '32px'
                            }}
                          >
                            <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All Jobs</MenuItem>
                            <MenuItem value="true" sx={{ fontSize: '0.875rem' }}>Archived</MenuItem>
                            <MenuItem value="false" sx={{ fontSize: '0.875rem' }}>Active</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {signedIn && !employerUserId && (
                      <Grid item>
                        <FormControlLabel
                          control={
                            <StyledSwitch
                              checked={favoritesOnly}
                              onChange={toggleFavoritesFilter}
                              icon={<FavoriteBorderIcon />}
                              checkedIcon={<FavoriteIcon />}
                            />
                          }
                          label='Favorites Only'
                          labelPlacement='start'
                          sx={{ marginRight: 0 }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {/* Content Area - Regular Job List */}
              <Grid container alignItems='flex-start' sx={{
                maxHeight: 'calc(100vh - 64px)',
                overflow: 'auto',
                flex: 1,
                width: '100%'
              }}>
                {/* Regular Job List */}
                <List
                  setPage={setPage}
                  page={page}
                  data={displayedData}
                  listType='job'
                  handleListItemClick={handleListItemClick}
                  dataTotal={totalJobs}
                  totalPages={totalPages}
                  signedIn={signedIn}
                  setDisplayedData={setDisplayedData}
                  onFavoriteClick={onFavoriteClick}
                  setActiveItem={setActiveItem}
                />
              </Grid>
            </Grid>

            {/* Resizable Divider */}
            <Box
              onMouseDown={startResize}
              sx={{
                display: { xs: 'none', md: 'block' },
                flexShrink: 0,
                width: '4px',
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '1px',
                  height: '100%',
                  backgroundColor: theme.palette.divider,
                },
                '&:hover, &:active': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            />

            {/* Right Panel */}
            <Grid
              container
              item
              xs={12}
              direction='column'
              sx={{
                height: 'calc(100vh - 64px)',
                minHeight: 'calc(100vh - 64px)',
                flex: { xs: '1', md: '1' },
                pl: { md: 3 },
                overflow: 'auto',
                display: { xs: 'none', md: 'flex' },
              }}
            >
              <JobContent
                item={activeItem}
                signedIn={signedIn}
                employerUserId={employerUserId}
                onFavoriteClick={onFavoriteClick}
                jobs={jobsList}
                activeItem={activeItem}
                onUploadMenuClick={handleUploadMenuClick}
                uploadMenuAnchor={uploadMenuAnchor}
                onUploadMenuClose={handleUploadMenuClose}
                onSmartUpload={handleSmartUpload}
                onManualUpload={handleManualUpload}
              />
            </Grid>
          </Box>
        )}
      </Grid>

      {/* Upload method selection menu */}
      <Menu
        anchorEl={uploadMenuAnchor}
        open={Boolean(uploadMenuAnchor)}
        onClose={handleUploadMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItemComponent onClick={handleManualUpload}>
          <UploadIcon sx={{ mr: 2 }} />
          Manual Upload
        </MenuItemComponent>
      </Menu>

      {/* Manual upload drawer */}
      <JobDrawer
        open={drawerJobOpen}
        onClose={() => setDrawerJobOpen(false)}
        isEdit={false}
        setOpenDrawer={setDrawerJobOpen}
      />

      {/* Smart upload drawer */}
      <SmartUploadDrawer
        open={smartUploadDrawerOpen}
        onClose={() => setSmartUploadDrawerOpen(false)}
      />

      {/* Success notification for newly uploaded jobs */}
      <Snackbar
        open={showUploadSuccess}
        autoHideDuration={6000}
        onClose={() => setShowUploadSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowUploadSuccess(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Successfully uploaded {uploadedJobsCount} job{uploadedJobsCount !== 1 ? 's' : ''}!
          Your new listings are now live.
        </Alert>
      </Snackbar>
    </>
  )
}

export default Jobs
